import { useCallback, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  errorText,
  nextTier,
  tierFor,
  TIERS,
  type HeadToHead,
  type LeaderboardEntry,
  type MatchSummary,
  type Rivalry,
} from '@chess-uno/shared-protocol'
import { getSocket, request } from '../api/socket.js'
import type { StoredSession } from '../api/session.js'
import { useI18n, type Messages } from '../i18n/index.js'
import { Avatar, Icon, TierMark } from './Chrome.js'

/** Challenge a player: creates the game, notifies the opponent and opens the waiting room. */
export function useChallenge(session: StoredSession | null): { challenge: (username: string) => Promise<void>; error: string | null } {
  const navigate = useNavigate()
  const { locale } = useI18n()
  const [error, setError] = useState<string | null>(null)
  const challenge = useCallback(
    async (username: string) => {
      if (!session) return
      const r = await request(getSocket(session), 'challenge:send', { username })
      if (r.ok) navigate(`/game/${r.data.gameId}`)
      else setError(errorText(r.error, locale))
    },
    [session, navigate, locale],
  )
  return { challenge, error }
}

export function timeAgo(iso: string, t: Messages): string {
  if (!iso) return ''
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return t.competition.justNow
  if (s < 3600) return t.competition.minutesAgo(Math.floor(s / 60))
  if (s < 86400) return t.competition.hoursAgo(Math.floor(s / 3600))
  return t.competition.daysAgo(Math.floor(s / 86400))
}

/**
 * Placeholder rows while a list loads: the block keeps its shape (avatar,
 * name, figures) with a soft shimmer, instead of an empty box.
 */
export function SkeletonList({ rows = 5, avatar = true }: { rows?: number; avatar?: boolean }) {
  const widths = [62, 48, 70, 54, 40, 66, 58, 45]
  return (
    <ul className="skeleton-list" aria-busy="true" aria-hidden="true" data-testid="skeleton">
      {Array.from({ length: rows }, (_, i) => (
        <li key={i}>
          {avatar && <span className="sk sk-avatar" />}
          <span className="sk-lines">
            <span className="sk sk-line" style={{ width: `${widths[i % widths.length]}%` }} />
            <span className="sk sk-line short" style={{ width: `${widths[(i + 3) % widths.length] / 2}%` }} />
          </span>
          <span className="sk sk-figure" />
        </li>
      ))}
    </ul>
  )
}

export function TierBadge({ rating, compact = false }: { rating: number; compact?: boolean }) {
  const { t } = useI18n()
  const tier = tierFor(rating).tier
  const label = t.common.tiers[tier]
  return (
    <span className={`tier tier-${tier}`} title={t.competition.tierTitle(label)}>
      <TierMark rating={rating} size={compact ? 12 : 13} />
      {!compact && <span>{label}</span>}
    </span>
  )
}

export function PlayerLink({ name }: { name: string }) {
  return (
    <Link className="player-link" to={`/player/${encodeURIComponent(name)}`}>
      {name}
    </Link>
  )
}

/** Current streak (from 2 wins). */
export function Streak({ streak }: { streak: number }) {
  const { t } = useI18n()
  if (streak < 2) return null
  return (
    <span className="streak" title={t.competition.streakTitle(streak)}>
      <Icon name="flame" size={12} />
      {streak}
    </span>
  )
}

/** Recent form: one dot per game, the most recent on the left. */
export function FormDots({ form }: { form: Array<'W' | 'L' | 'D'> }) {
  const { t } = useI18n()
  const labels = t.competition.form
  return (
    <span className="form" aria-label={t.competition.formAria(form.map((f) => labels[f]))}>
      {form.map((f, i) => (
        <span key={i} className={`form-dot form-${f}`} title={labels[f]} />
      ))}
    </span>
  )
}

/** Balance of power: wins / draws / losses. */
export function ScoreBar({ wins, draws, losses }: { wins: number; draws: number; losses: number }) {
  const total = wins + draws + losses || 1
  return (
    <span className="score-bar" aria-hidden="true">
      <span className="sb-w" style={{ width: `${(wins / total) * 100}%` }} />
      <span className="sb-d" style={{ width: `${(draws / total) * 100}%` }} />
      <span className="sb-l" style={{ width: `${(losses / total) * 100}%` }} />
    </span>
  )
}

/** A player's rating: large Elo, tier, rank, progress, record. */
export function RatingHero({
  entry,
  rankedPlayers,
  eyebrow,
}: {
  entry: LeaderboardEntry
  rankedPlayers: number
  eyebrow?: string
}) {
  const { t } = useI18n()
  const next = nextTier(entry.rating)
  const nextLabel = next ? t.common.tiers[next.tier] : ''
  const current = tierFor(entry.rating)
  const floor = Number.isFinite(current.min) ? current.min : entry.rating - 100
  const progress = next ? Math.min(100, Math.max(3, ((entry.rating - floor) / (entry.rating + next.missing - floor)) * 100)) : 100
  const winRate = entry.games ? Math.round((entry.wins / entry.games) * 100) : null
  return (
    <section className={`rating-hero tier-${current.tier}`} data-testid="my-card">
      <p className="eyebrow">{eyebrow ?? t.competition.yourRanking}</p>
      <div className="rating-line">
        <span className="rating-value" data-testid="my-rating">
          {entry.rating}
        </span>
        <div className="rating-meta">
          <TierBadge rating={entry.rating} />
          <span className="rating-rank" data-testid="my-rank">
            {entry.rank ? (
              <>
                #{entry.rank} <span className="muted">{t.competition.outOf(rankedPlayers)}</span>
              </>
            ) : (
              <span className="muted">{t.competition.unranked}</span>
            )}
          </span>
        </div>
      </div>
      <div className="tier-track" title={next ? t.competition.pointsFor(next.missing, nextLabel) : t.competition.maxTier}>
        <span style={{ width: `${progress}%` }} />
      </div>
      <p className="tier-caption">
        {next ? t.competition.pointsBefore(next.missing, nextLabel) : t.competition.maxTierCaption}
      </p>
      <dl className="stat-row">
        <div>
          <dt>{t.competition.wins}</dt>
          <dd>{entry.wins}</dd>
        </div>
        <div>
          <dt>{t.competition.losses}</dt>
          <dd>{entry.losses}</dd>
        </div>
        <div>
          <dt>{t.competition.draws}</dt>
          <dd>{entry.draws}</dd>
        </div>
        <div>
          <dt>{t.competition.winRate}</dt>
          <dd>{winRate === null ? '—' : t.competition.percent(winRate)}</dd>
        </div>
        <div>
          <dt>{t.competition.streak}</dt>
          <dd>
            {entry.streak > 0 ? entry.streak : '—'}
            {entry.bestStreak >= 2 && <small> {t.competition.best(entry.bestStreak)}</small>}
          </dd>
        </div>
      </dl>
    </section>
  )
}

export function Leaderboard({
  entries,
  meId,
  onChallenge,
  compact = false,
  loading = false,
}: {
  entries: LeaderboardEntry[]
  meId?: string
  onChallenge?: (name: string) => void
  compact?: boolean
  loading?: boolean
}) {
  const { t } = useI18n()
  if (loading) return <SkeletonList rows={compact ? 5 : 8} />
  if (entries.length === 0) return <p className="empty">{t.competition.leaderboardEmpty}</p>
  return (
    <ol className={`leaderboard${compact ? ' compact' : ''}`} data-testid="leaderboard">
      {entries.map((e) => (
        <li key={e.playerId} className={`lb-row${e.playerId === meId ? ' me' : ''}${e.rank <= 3 ? ` podium-${e.rank}` : ''}`}>
          <span className="lb-rank">{e.rank}</span>
          <span className="lb-player">
            <Avatar name={e.name} size={28} />
            <span className="lb-id">
              <span className="lb-name">
                <PlayerLink name={e.name} />
                {e.playerId === meId && <span className="you-tag">{t.competition.you}</span>}
                <Streak streak={e.streak} />
              </span>
              {!compact && (
                <span className="lb-record">
                  {t.competition.record(e.wins, e.losses, e.draws)}
                </span>
              )}
            </span>
          </span>
          <span className="lb-rating">
            <TierMark rating={e.rating} size={12} />
            {e.rating}
          </span>
          {onChallenge && (
            <span className="lb-action">
              {e.playerId !== meId && (
                <button type="button" className="ghost small-btn" onClick={() => onChallenge(e.name)} data-testid={`challenge-${e.name}`}>
                  {t.competition.challenge}
                </button>
              )}
            </span>
          )}
        </li>
      ))}
    </ol>
  )
}

/** Teasing line: where do you stand against this opponent? */
export function taunt(h: HeadToHead, t: Messages): string {
  const diff = h.wins - h.losses
  const say = t.competition.taunts
  if (h.games === 0) return say.never
  if (h.form[0] === 'L' && h.form[1] === 'L') return say.twoLosses
  if (diff >= 3) return say.dominate
  if (diff > 0) return say.lead
  if (diff === 0) return say.tied
  if (diff <= -3) return say.escape
  return say.behind
}

/** Head-to-head records, opponent by opponent. */
export function RivalsList({
  rivals,
  onChallenge,
  taunts = true,
  loading = false,
}: {
  rivals: HeadToHead[]
  onChallenge?: (name: string) => void
  /** Second-person sentences: only for one's own head-to-head records. */
  taunts?: boolean
  loading?: boolean
}) {
  const { t } = useI18n()
  if (loading) return <SkeletonList rows={3} />
  if (rivals.length === 0) return <p className="empty">{t.competition.rivalsEmpty}</p>
  return (
    <ul className="rivals" data-testid="rivals">
      {rivals.map((h) => {
        const lead = h.wins > h.losses ? 'lead' : h.wins < h.losses ? 'behind' : 'tied'
        return (
          <li key={h.opponentId} className={`rival ${lead}`}>
            <Avatar name={h.opponentName} size={32} />
            <div className="rival-body">
              <div className="rival-head">
                <PlayerLink name={h.opponentName} />
                <span className="rival-score" data-testid={`h2h-${h.opponentName}`}>
                  {h.wins} – {h.losses}
                </span>
              </div>
              <ScoreBar wins={h.wins} draws={h.draws} losses={h.losses} />
              <div className="rival-foot">
                <FormDots form={h.form} />
                {taunts && <span className="rival-taunt">{taunt(h, t)}</span>}
                {h.draws > 0 && <span className="rival-draws">{t.competition.drawsShort(h.draws)}</span>}
              </div>
            </div>
            {onChallenge && (
              <button type="button" className="ghost small-btn" onClick={() => onChallenge(h.opponentName)}>
                {lead === 'behind' ? t.competition.revenge : t.competition.replay}
              </button>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/** Big rivalries: the most contested pairs on the server. */
export function RivalriesList({ rivalries, loading = false }: { rivalries: Rivalry[]; loading?: boolean }) {
  const { t } = useI18n()
  if (loading) return <SkeletonList rows={3} avatar={false} />
  if (rivalries.length === 0) return <p className="empty">{t.competition.rivalriesEmpty}</p>
  return (
    <ul className="rivalries">
      {rivalries.map((r) => (
        <li key={`${r.a.id}|${r.b.id}`}>
          <span className={`rv-side${r.a.wins > r.b.wins ? ' leader' : ''}`}>
            <PlayerLink name={r.a.name} />
          </span>
          <span className="rv-score">
            {r.a.wins}
            <i>–</i>
            {r.b.wins}
          </span>
          <span className={`rv-side right${r.b.wins > r.a.wins ? ' leader' : ''}`}>
            <PlayerLink name={r.b.name} />
          </span>
          <span className="rv-meta">
            {t.competition.rivalryMeta(r.games, r.draws)}
          </span>
        </li>
      ))}
    </ul>
  )
}

/** Latest results (neutral, or from a player's point of view with `perspective`). */
export function RecentMatches({ matches, perspective, loading = false }: { matches: MatchSummary[]; perspective?: string; loading?: boolean }) {
  const { t } = useI18n()
  if (loading) return <SkeletonList rows={5} avatar={false} />
  if (matches.length === 0) return <p className="empty">{t.competition.recentEmpty}</p>
  return (
    <ul className="feed">
      {matches.map((m) => {
        const delta = (i: 0 | 1) => m.ratingAfter[i] - m.ratingBefore[i]
        const signed = (d: number) => (d > 0 ? `+${d}` : `${d}`)
        const found = perspective ? m.players.findIndex((p) => p.id === perspective) : -1
        const mine = found === 0 || found === 1 ? found : null
        const outcome = mine === null ? null : m.winner === null ? 'D' : m.winner === mine ? 'W' : 'L'
        const shownDelta = mine !== null ? delta(mine) : m.winner !== null ? delta(m.winner) : null
        const markClass = outcome ?? (m.winner === null ? 'D' : 'W')
        return (
          <li key={m.gameId}>
            <span className={`feed-mark form-${markClass}`} />
            <span className="feed-text">
              {m.winner === null ? (
                <>
                  <PlayerLink name={m.players[0].name} /> <span className="muted">{t.competition.and}</span> <PlayerLink name={m.players[1].name} />{' '}
                  <span className="muted">{t.competition.drew}</span>
                </>
              ) : (
                <>
                  <PlayerLink name={m.players[m.winner].name} /> <span className="muted">{t.competition.beat}</span>{' '}
                  <PlayerLink name={m.players[m.winner === 0 ? 1 : 0].name} />
                </>
              )}
              <span className="feed-meta">
                {t.competition.reasons[m.reason] ?? m.reason} · {timeAgo(m.finishedAt, t)}
              </span>
            </span>
            {shownDelta !== null && <span className={`delta ${shownDelta >= 0 ? 'up' : 'down'}`}>{signed(shownDelta)}</span>}
          </li>
        )
      })}
    </ul>
  )
}

/** Tier legend. */
export function TierLegend() {
  const { t } = useI18n()
  return (
    <p className="tier-legend">
      {[...TIERS].reverse().map((tier) => (
        <span key={tier.tier} className={`tier tier-${tier.tier}`}>
          <TierMark rating={Number.isFinite(tier.min) ? tier.min : 0} size={12} />
          {t.common.tiers[tier.tier]}
          {Number.isFinite(tier.min) && <small>{tier.min}</small>}
        </span>
      ))}
    </p>
  )
}
